// ---------------------------------------------------------------------------
// Pure date-range math for AEMET/ROCIO fetches — split out of
// aemetJobStore.ts (see project_evalhid_code_audit.md's ongoing "god
// module" breakup) since none of this needs the store's own state, just
// `AemetJob`'s shape — imported from aemetJobTypes.ts, not aemetJobStore.ts
// itself, so this file has no dependency on the store module at all (not
// even a type-only one — deep-review, 2026-08-29: the type-only back-edge
// this used to have was erased at compile time, never a real runtime cycle,
// but still fragile — a future value import from aemetJobStore.ts would
// have turned it into a genuine one).
// ---------------------------------------------------------------------------
import { enumerateWindows, type DateRange } from "./aemet.ts";
import type { ProjectSeries } from "../dailySeries.ts";
import { ROCIO_COVERAGE_END, ROCIO_COVERAGE_START } from "../rocio.ts";
import type { AemetJob, AemetJobPhase } from "./aemetJobTypes.ts";

function isoDaysAgo(days: number): string {
  return new Date(Date.now() - days * 86_400_000).toISOString().slice(0, 10);
}

/** Exported for AemetBulkFetchDialog.tsx, which needs the same default to pre-fill its shared "ask once" date range when the project has no series yet — see `deriveRangeInfo`'s own free-range fallback below. */
export function defaultRangeEnd(): string {
  return isoDaysAgo(1);
}

export function defaultRangeStart(): string {
  const d = new Date(Date.now() - 86_400_000);
  d.setUTCFullYear(d.getUTCFullYear() - 30);
  return d.toISOString().slice(0, 10);
}

/**
 * Clamps a free date range into ROCIO's real coverage
 * (`ROCIO_COVERAGE_START`/`END`, rocio.ts) — the generic free-range default
 * (last 30 years through yesterday, `defaultRangeStart`/`defaultRangeEnd`
 * above) makes sense for AEMET's live station data, but ROCIO's static grid
 * stops at 2022-12-31: without this, a fresh "rocio" fetch would silently
 * request years the grid has no data for at all (every one of those days
 * would come back "sin dato", with nothing telling the user why — see
 * AemetFetchDialog.tsx's own ROCIO range warning). Only ever applied when
 * the method IS "rocio" — station-based methods keep the generic default,
 * they have no such upper bound.
 */
export function clampToRocioCoverage(range: DateRange): DateRange {
  return {
    start: range.start < ROCIO_COVERAGE_START ? ROCIO_COVERAGE_START : range.start,
    end: range.end > ROCIO_COVERAGE_END ? ROCIO_COVERAGE_END : range.end,
  };
}

/**
 * Phases where a fresh "start" should re-fetch stations from scratch
 * instead of just reopening what's already there — nothing in flight,
 * nothing worth keeping. Also, from the other direction, exactly the set
 * of "finished" jobs AemetJobsPopover.tsx sorts to the bottom of the list
 * and bulk-dismisses with its own "Clear finished" button — a job is
 * restartable precisely because it's done (successfully or not), so the
 * two questions share one answer.
 */
export function isRestartable(phase: AemetJobPhase): boolean {
  return (
    phase === "report" ||
    phase === "stations-error" ||
    phase === "download-error" ||
    phase === "no-stations"
  );
}

/**
 * AemetBulkFetchDialog.tsx's "extend range" batch status, derived from
 * `aemetJobStore`'s own live jobs — pulled out as a pure function (rather
 * than staying inline in `AemetExtendBody`) so it's directly unit-testable
 * without a React environment (this codebase has no React Testing Library —
 * see the header comment on `test/ui/useProjectPersistence.test.ts` for the
 * same reasoning).
 *
 * Real bug this replaced: the dialog used to compute its own "current
 * range"/validation straight from the project's LIVE series
 * (`deriveRangeInfo`, re-evaluated every render) and track batch progress
 * with local `useState`. Once "Extend all" starts, results stream back in
 * sub-basin by sub-basin — the shared date axis grows the moment the FIRST
 * one finishes, long before the rest do, so the live-recomputed range shifted
 * under the user while a real download was still running underneath,
 * eventually flipping validation to "invalid" for no real reason. None of it
 * survived closing and reopening the dialog either, since it lived in that
 * component's own state.
 *
 * The fix: every job in one batch starts with the IDENTICAL `extendRange`,
 * fixed at the moment `aemetJobStore.startQueued` was called, and jobs stay
 * in the store (with `extendRange` intact) until the user dismisses them,
 * finished or not — so reading straight from the store gives both a stable
 * range to display/validate against and a real, accurate progress count,
 * and it survives a remount for free since the store isn't component state.
 */
export function extendBatchStatus(
  jobs: readonly Pick<AemetJob, "subBasinCode" | "extendRange" | "phase">[],
  rowCodes: ReadonlySet<number>
): { total: number; done: number; running: boolean; range: DateRange | null } {
  const batchJobs = jobs.filter((j) => rowCodes.has(j.subBasinCode) && j.extendRange !== null);
  const total = batchJobs.length;
  const done = batchJobs.filter((j) => isRestartable(j.phase)).length;
  const running = total > 0 && done < total;
  const range = running
    ? (batchJobs.find((j) => j.extendRange !== null)?.extendRange ?? null)
    : null;
  return { total, done, running, range };
}

/**
 * Range/target-dates bookkeeping shared by the store (download/apply) and
 * the UI (display) — see the exact-date-match contract in aemetJobStore.ts's
 * own header comment. Takes `job` as possibly `undefined` (before a job for
 * this sub-basin has ever been created) and falls back to a fresh default
 * range so the UI has something sane to show even then, though in practice
 * `AemetFetchDialog` never actually renders before `start()` has created
 * the job (see EditSubBasinDialog.tsx's button).
 */
export function deriveRangeInfo(
  job: Pick<AemetJob, "freeStart" | "freeEnd"> | undefined,
  series: ProjectSeries
): {
  forcedRange: DateRange | null;
  range: DateRange;
  validRange: boolean;
  windowCount: number;
  targetDates: string[];
} {
  const existingDates = series.precipitation?.dates ?? null;
  const forcedRange: DateRange | null =
    existingDates && existingDates.length > 0
      ? { start: existingDates[0], end: existingDates[existingDates.length - 1] }
      : null;
  const freeStart = job?.freeStart ?? defaultRangeStart();
  const freeEnd = job?.freeEnd ?? defaultRangeEnd();
  const range: DateRange = forcedRange ?? { start: freeStart, end: freeEnd };
  const validRange = range.start <= range.end;
  const windowCount = validRange ? enumerateWindows(range).length : 0;
  const targetDates = existingDates ?? enumerateWindows(range, 1).map((w) => w.start);
  return { forcedRange, range, validRange, windowCount, targetDates };
}

/** `deriveRangeInfo`, except for an extend-mode job (`job.extendRange` set) — bypasses the forced/free-range choice entirely in favor of the explicit window AemetBulkFetchDialog.tsx already resolved. */
export function rangeInfoForJob(
  job: AemetJob,
  series: ProjectSeries
): { range: DateRange; windowCount: number; validRange: boolean; targetDates: string[] } {
  if (!job.extendRange) return deriveRangeInfo(job, series);
  const validRange = job.extendRange.start <= job.extendRange.end;
  return {
    range: job.extendRange,
    windowCount: validRange ? enumerateWindows(job.extendRange).length : 0,
    validRange,
    targetDates: validRange ? enumerateWindows(job.extendRange, 1).map((w) => w.start) : [],
  };
}
